/**
 * TMF9 Copa Energia - Rotas do Cliente
 *
 * Registra todas as rotas /copa/* no React Router do TMF9 base.
 * Carregado automaticamente via import.meta.glob em App.tsx quando
 * VITE_CLIENT_CODE=copa.
 *
 * Jira: CE-2256
 */

import React, { lazy } from 'react';
import { Route, useParams } from 'react-router-dom';

// ─── Lazy imports ────────────────────────────────────────────────────────────

// Frota
const FrotaList = lazy(() => import('./pages/FrotaList').then(m => ({ default: m.FrotaList })));

// PEX
const PexDashboard          = lazy(() => import('./pages/pex/PexDashboard').then(m => ({ default: m.PexDashboard })));
const PexAcompanhamento      = lazy(() => import('./pages/pex/PexAcompanhamento').then(m => ({ default: m.PexAcompanhamento })));
const PexDesempenhoIndividual = lazy(() => import('./pages/pex/PexDesempenhoIndividual').then(m => ({ default: m.PexDesempenhoIndividual })));
const PexRanking             = lazy(() => import('./pages/pex/PexRanking').then(m => ({ default: m.PexRanking })));
const PexPremiacao           = lazy(() => import('./pages/pex/PexPremiacao').then(m => ({ default: m.PexPremiacao })));
const PexAdmin               = lazy(() => import('./pages/pex/PexAdmin').then(m => ({ default: m.PexAdmin })));
const PexImportacoes         = lazy(() => import('./pages/pex/PexImportacoes').then(m => ({ default: m.PexImportacoes })));
const PexDesempenhoUsuario2026 = lazy(() => import('./pages/pex/PexDesempenhoUsuario2026').then(m => ({ default: m.PexDesempenhoUsuario2026 })));

// Promoção Liquigás
const PromoCadastro      = lazy(() => import('./pages/promo/PromoCadastro').then(m => ({ default: m.PromoCadastro })));
const PromoResgate       = lazy(() => import('./pages/promo/PromoResgate').then(m => ({ default: m.PromoResgate })));
const PromoNumeros       = lazy(() => import('./pages/promo/PromoNumeros').then(m => ({ default: m.PromoNumeros })));
const PromoGanhadores    = lazy(() => import('./pages/promo/PromoGanhadores').then(m => ({ default: m.PromoGanhadores })));
const PromoDashboardAdmin = lazy(() => import('./pages/promo/PromoDashboardAdmin').then(m => ({ default: m.PromoDashboardAdmin })));

// Padronização
const PadronizacaoDashboard = lazy(() => import('./pages/padronizacao/PadronizacaoDashboard').then(m => ({ default: m.PadronizacaoDashboard })));
const PadronizacaoImport    = lazy(() => import('./pages/padronizacao/PadronizacaoImport').then(m => ({ default: m.PadronizacaoImport })));

// Energizados 2025
const EnergizadosDashboard  = lazy(() => import('./pages/energizados/EnergizadosDashboard').then(m => ({ default: m.EnergizadosDashboard })));
const EnergizadosCertificados = lazy(() => import('./pages/energizados/EnergizadosCertificados').then(m => ({ default: m.EnergizadosCertificados })));

// Fale Conosco
const ContactsDashboard = lazy(() => import('./pages/contacts/ContactsDashboard').then(m => ({ default: m.ContactsDashboard })));

// SSO
const SSOConfig = lazy(() => import('./pages/sso/SSOConfig').then(m => ({ default: m.SSOConfig })));

// Importações
const ImportsDashboard = lazy(() => import('./pages/imports/ImportsDashboard').then(m => ({ default: m.ImportsDashboard })));

// Meus Dados
const MeusDados = lazy(() => import('./pages/pessoa/MeusDados').then(m => ({ default: m.MeusDados })));

// Portal SAC
const SacReportPage = lazy(() => import('./pages/portal/SacReportPage').then(m => ({ default: m.SacReportPage })));

// ─── Wrappers para componentes que recebem props em vez de useParams ──────────

const ContactDetailPage = lazy(() =>
  import('./pages/contacts/ContactDetail').then(m => ({
    default: function ContactDetailPage() {
      const { id } = useParams<{ id: string }>();
      return <m.ContactDetail contactId={Number(id)} />;
    },
  }))
);

const PadronizacaoApprovalPage = lazy(() =>
  import('./pages/padronizacao/PadronizacaoApproval').then(m => ({
    default: function PadronizacaoApprovalPage() {
      const { id } = useParams<{ id: string }>();
      return <m.PadronizacaoApproval id={Number(id)} />;
    },
  }))
);

const TermoFrotaPage = lazy(() =>
  import('./pages/padronizacao/TermoFrota').then(m => ({
    default: function TermoFrotaPage() {
      const { id } = useParams<{ id: string }>();
      return <m.TermoFrota idFrota={Number(id)} />;
    },
  }))
);

// ─── Rotas ───────────────────────────────────────────────────────────────────

export const clientRoutes = (
  <>
    {/* Frota */}
    <Route path="/copa/frota"            element={<FrotaList />} />

    {/* PEX */}
    <Route path="/copa/pex"              element={<PexDashboard />} />
    <Route path="/copa/pex/acompanhamento" element={<PexAcompanhamento />} />
    <Route path="/copa/pex/desempenho/:id" element={<PexDesempenhoIndividual />} />
    <Route path="/copa/pex/ranking"      element={<PexRanking />} />
    <Route path="/copa/pex/premiacao"    element={<PexPremiacao />} />
    <Route path="/copa/pex/admin"        element={<PexAdmin />} />
    <Route path="/copa/pex/importacoes"  element={<PexImportacoes />} />
    <Route path="/copa/pex/2026"         element={<PexDesempenhoUsuario2026 />} />

    {/* Promoção Liquigás */}
    <Route path="/copa/promo-liquigas"            element={<PromoCadastro />} />
    <Route path="/copa/promo-liquigas/resgate"    element={<PromoResgate />} />
    <Route path="/copa/promo-liquigas/numeros"    element={<PromoNumeros />} />
    <Route path="/copa/promo-liquigas/ganhadores" element={<PromoGanhadores />} />
    <Route path="/copa/promo-liquigas/dashboard"  element={<PromoDashboardAdmin />} />

    {/* Padronização Visual */}
    <Route path="/copa/padronizacao"                    element={<PadronizacaoDashboard />} />
    <Route path="/copa/padronizacao/moderacao/:id"      element={<PadronizacaoApprovalPage />} />
    <Route path="/copa/padronizacao/importacao"         element={<PadronizacaoImport />} />
    <Route path="/copa/padronizacao/termo-frota/:id"    element={<TermoFrotaPage />} />

    {/* Energizados 2025 */}
    <Route path="/copa/energizados"             element={<EnergizadosDashboard />} />
    <Route path="/copa/energizados/certificados" element={<EnergizadosCertificados />} />
    <Route path="/copa/energizados/:id"         element={<EnergizadosDashboard />} />

    {/* Fale Conosco */}
    <Route path="/copa/contacts"     element={<ContactsDashboard />} />
    <Route path="/copa/contacts/:id" element={<ContactDetailPage />} />

    {/* SSO */}
    <Route path="/copa/sso" element={<SSOConfig />} />

    {/* Importações */}
    <Route path="/copa/imports" element={<ImportsDashboard />} />

    {/* Meus Dados */}
    <Route path="/copa/meus-dados" element={<MeusDados />} />

    {/* Portal > SAC */}
    <Route path="/copa/portal/sac/relatorio" element={<SacReportPage />} />
  </>
);
